import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import TopNav from "../components/TopNav.jsx";
import MatchDetector from "../components/MatchDetector.jsx";

export default function LibraryPage() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [filter, setFilter] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const d = await api.libraryList();
        if (!cancelled) setData(d);
      } catch (e) {
        if (!cancelled) setErr(e.message);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const corpora = (data?.corpora || []).filter((c) => {
    if (!filter) return true;
    const q = filter.toLowerCase();
    return (
      c.name?.toLowerCase().includes(q) ||
      c.description?.toLowerCase().includes(q) ||
      (c.tags || []).some((t) => t.toLowerCase().includes(q))
    );
  });

  return (
    <div className="min-h-screen bg-sage-bg text-sage-text sage-grain">
      <TopNav />
      <div className="mx-auto max-w-7xl px-4 md:px-8 py-8">
        <div className="border-b border-sage-border pb-6 mb-6">
          <div className="text-xs uppercase tracking-[0.2em] text-sage-muted mb-2">
            The Library · curated free-source shelves
          </div>
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tighter uppercase text-sage-text">
            Pick a shelf. Read the sources.
          </h1>
          <div className="text-sm text-sage-muted mt-3 max-w-3xl leading-relaxed">
            Each shelf lists free, primary sources on one subject — the site, publisher, year, and a short educational description. Every shelf publishes its own bias card so you can see what the shelf covers, what it leaves out, and where to read counter-arguments. Every source citation is a live link that opens on the publisher's own page.
          </div>
        </div>

        <div className="flex justify-between items-center mb-6 gap-3 flex-wrap">
          <input
            type="text"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Filter by name, tag, or description…"
            data-testid="library-filter"
            className="border border-sage-border bg-sage-bg text-sage-text px-3 py-2 text-sm w-full md:w-96"
          />
          <div className="text-xs text-sage-faint tabular-nums">
            {corpora.length} shelf{corpora.length === 1 ? "" : "es"} · {data?.count ?? "…"} total
          </div>
        </div>

        {err && (
          <div className="border border-sage-err text-sage-err p-4 text-xs" data-testid="library-error">
            Failed to load: {err}
          </div>
        )}

        {!data && !err && (
          <div className="text-sage-faint text-xs" data-testid="library-loading">Loading shelves…</div>
        )}

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="library-grid">
          {corpora.map((c) => (
            <Link
              key={c.slug}
              to={`/library/${c.slug}`}
              data-testid={`library-tile-${c.slug}`}
              className="border border-sage-border p-5 hover:border-sage-accent hover:bg-sage-accent/5 transition-colors block group"
            >
              <div className="flex justify-between items-baseline mb-2">
                <div className="text-lg font-bold uppercase tracking-tight text-sage-text group-hover:text-sage-accent transition-colors">
                  {c.name}
                </div>
                <div className="text-[10px] tabular-nums text-sage-faint uppercase tracking-[0.1em]">
                  {c.source_count} src
                </div>
              </div>
              <div className="text-xs text-sage-muted mb-3 leading-relaxed">
                {c.description}
              </div>
              <div className="flex flex-wrap gap-1 mb-3">
                {(c.tags || []).slice(0, 6).map((t) => (
                  <span key={t} className="text-[10px] uppercase tracking-[0.1em] text-sage-faint border border-sage-border px-2 py-0.5">
                    {t}
                  </span>
                ))}
              </div>
              {c.has_bias_card && (
                <div className="text-[10px] uppercase tracking-[0.15em] text-sage-accent">
                  ⓘ Bias card published
                </div>
              )}
            </Link>
          ))}
        </div>

        {data && corpora.length === 0 && (
          <div className="p-8 text-center text-sage-faint text-sm border border-sage-border" data-testid="library-empty">
            No shelves match. Try a different filter.
          </div>
        )}

        <div className="mt-10 pt-6 border-t border-sage-border">
          <MatchDetector />
        </div>

        <div className="mt-10 pt-6 border-t border-sage-border">
          <div className="text-[10px] uppercase tracking-[0.2em] text-sage-muted mb-3">
            How to read a shelf
          </div>
          <div className="grid md:grid-cols-3 gap-4 text-xs text-sage-muted">
            <div className="border border-sage-border p-4">
              <div className="text-sage-text uppercase tracking-[0.15em] text-[10px] mb-2">Bias card</div>
              Each shelf opens with a plain-language description of what the shelf covers, whose viewpoints it centres, and where opposing views can be read elsewhere.
            </div>
            <div className="border border-sage-border p-4">
              <div className="text-sage-text uppercase tracking-[0.15em] text-[10px] mb-2">Source citations</div>
              Every entry lists title, publisher, publication year, and a one-paragraph description written in neutral language. Click through to the publisher's own page.
            </div>
            <div className="border border-sage-border p-4">
              <div className="text-sage-text uppercase tracking-[0.15em] text-[10px] mb-2">Retraction log</div>
              When a source is removed from a shelf, an entry is retained noting the removal date and the reason.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
